import { BRAND, renderActivityReportHtml, renderJobReportHtml, type ActivityReportRow, type JobReportRow } from '@crew/shared';

/**
 * דוח PDF ממותג לעבודה.
 *
 * התבנית (renderJobReportHtml, משותפת) מוכנה להדפסה ב-A4, עם עברית מלאה ו-RTL.
 * הדוח נבנה מהנתונים העדכניים ברגע הלחיצה ונפתח בחלון ההדפסה של המכשיר:
 * - מחשב: "שמירה כ-PDF"
 * - אנדרואיד: "שמירה כ-PDF" בחלון ההדפסה
 * - אייפון: "הדפסה" ← צביטה החוצה על התצוגה המקדימה ← שיתוף / שמירה בקבצים
 * כך העברית מוצגת בדיוק כמו בדפדפן, בלי ספריית PDF שמתקשה עם RTL.
 */

let logoCache: string | null = null;

async function logoDataUri(): Promise<string> {
  if (logoCache) return logoCache;
  try {
    const res = await fetch(BRAND.assets.logoFull);
    const blob = await res.blob();
    logoCache = await new Promise<string>((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result));
      r.onerror = reject;
      r.readAsDataURL(blob);
    });
    return logoCache;
  } catch {
    return BRAND.assets.logoFull;
  }
}

export function reportFileName(job: { name: string; date: string }): string {
  const safe = job.name.replace(/[\\/:*?"<>|]+/g, ' ').trim().slice(0, 60);
  return `${BRAND.name} - ${safe} - ${job.date.split('-').reverse().join('.')}`;
}

export async function printJobReport(job: { name: string; date: string; reportTime: string; location: string }, rows: JobReportRow[]): Promise<void> {
  const html = renderJobReportHtml({ job, rows, generatedAt: new Date(), logoSrc: await logoDataUri() });
  await printHtml(html, reportFileName(job));
}

/** יומן הפעילות של עבודה אחת כ-PDF (דרך חלון ההדפסה). */
export async function printActivityReport(job: { name: string; date: string; reportTime: string; location: string }, rows: ActivityReportRow[]): Promise<void> {
  const html = renderActivityReportHtml({ job, rows, generatedAt: new Date(), logoSrc: await logoDataUri() });
  await printHtml(html, `${reportFileName(job)} - יומן פעילות`);
}

/** מדפיס מסמך HTML מלא דרך iframe נסתר. שם הקובץ המוצע נלקח מכותרת המסמך. */
async function printHtml(html: string, title: string): Promise<void> {
  const frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden';
  document.body.appendChild(frame);
  const doc = frame.contentDocument!;
  doc.open();
  doc.write(html.replace(/<title>[^<]*<\/title>/, `<title>${title.replace(/</g, '')}</title>`));
  doc.close();

  await new Promise<void>((resolve) => {
    const img = doc.querySelector('img');
    if (!img || img.complete) return resolve();
    img.onload = () => resolve();
    img.onerror = () => resolve();
  });
  // המתנה קצרה לגופנים
  await (doc.fonts?.ready ?? Promise.resolve());

  const previousTitle = document.title;
  document.title = title;
  frame.contentWindow!.focus();
  frame.contentWindow!.print();
  setTimeout(() => {
    document.title = previousTitle;
    frame.remove();
  }, 60_000);
}
